import {
    APP_EMITTER_EVENTS,
    AppEmitter,
    globalCacheContext,
    AsyncSearchableSelect,
    DynamicInput,
    FormLabel,
    IValidateError,
    LinearProgress,
    OptionType,
    SearchableSelect,
} from "shared";
import { ITemplateCounterpartyComponent } from "entities/template";
import { authContext, propertiesContext } from "entities/user";
import { documentService, IVendorBank, IVendor } from "entities/document";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import { useParams } from "react-router-dom";
import { CounterpartyDocumentSelectFields } from "../../../../lib/const/counterparty_document_select_fields";
import { IValidationFunc } from "../../../../lib/types/validation";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { VendorFields } from "../../../../lib/const/vendor_fields";
import { constructOptions } from "../../../../lib/utils/dynamic_document_form_utils";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import styles from "./document_form_counterparty.module.scss";

interface IDocumentFormCounterpartyProps {
    component: ITemplateCounterpartyComponent;
    externalUpdateRecord: Record<string, any>;
    onChange: (partial: Record<string, any>) => void;
    onValidationRegister: (validationFunc: IValidationFunc, tag: string) => void;
    label?: string;
    tooltip: IComponentTooltipSettings;
    readonly: boolean;
    isExternalProtected: boolean;
    triggerValidate: number;
}

export const DocumentFormCounterparty: React.FC<IDocumentFormCounterpartyProps> = React.memo(({
                                                                                                  component,
                                                                                                  label,
                                                                                                  triggerValidate,
                                                                                                  externalUpdateRecord,
                                                                                                  tooltip,
                                                                                                  readonly,
                                                                                                  isExternalProtected,
                                                                                                  onValidationRegister,
                                                                                                  onChange,
                                                                                              }) => {
    const [inn, setInn] = useState<string>("");
    const [vendor, setVendor] = useState<IVendor>();
    const [address, setAddress] = useState<OptionType>();
    const [addressOptions, setAddressOptions] = useState<OptionType[]>([]);
    const [vendorName, setVendorName] = useState<OptionType>();
    const [bankDetails, setBankDetails] = useState<IVendorBank[]>([]);
    const [bankName, setBankName] = useState<OptionType>();
    const [bankNameOptions, setBankNameOptions] = useState<OptionType[]>([]);
    const [vendorSite, setVendorSite] = useState<OptionType>();
    const [vendorSiteOptions, setVendorSiteOptions] = useState<OptionType[]>([]);
    const [showInnMessage, setShowInnMessage] = useState<boolean>(false);
    const [bic, setBic] = useState<OptionType>();
    const [bicOptions, setBicOptions] = useState<OptionType[]>([]);
    const [paymentAccount, setPaymentAccount] = useState<OptionType>();
    const [paymentAccountOptions, setPaymentAccountOptions] = useState<OptionType[]>([]);
    const [corrAccountOptions, setCorrAccountOptions] = useState<OptionType[]>([]);
    const [corrAccount, setCorrAccount] = useState<OptionType>();
    const [record, setRecord] = useState<Record<string, string | undefined>>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [useExternalValues, setUseExternalValues] = useState<boolean>(false);
    const { i18n } = useTranslation();
    const t = useComponentsTranslations();
    const { extUserId } = useParams();
    const userInfo = useContext(authContext);
    const globalCache = useContext(globalCacheContext);
    const { getProperty } = useContext(propertiesContext);

    const selectFields = useMemo(() => Object.values(CounterpartyDocumentSelectFields) as string[], []);
    const selectVariablesMapping: Record<CounterpartyDocumentSelectFields, any> = {
        [CounterpartyDocumentSelectFields.vendor]: [vendorName, setVendorName],
        [CounterpartyDocumentSelectFields.vendorAddress]: [address, setAddress, addressOptions],
        [CounterpartyDocumentSelectFields.vendorSite]: [vendorSite, setVendorSite, vendorSiteOptions],
        [CounterpartyDocumentSelectFields.vendorBankName]: [bankName, setBankName, bankNameOptions],
        [CounterpartyDocumentSelectFields.vendorBic]: [bic, setBic, bicOptions],
        [CounterpartyDocumentSelectFields.vendorPaymentAccount]: [paymentAccount, setPaymentAccount, paymentAccountOptions],
        [CounterpartyDocumentSelectFields.vendorCorrAccount]: [corrAccount, setCorrAccount, corrAccountOptions],
    };

    const isExternal = useMemo(() => {
        return !!userInfo && userInfo?.isExternal === true;
    }, [userInfo]);

    const isCreatable = useMemo(() => {
        return !component.selectonly;
    }, [component.selectonly]);

    const componentSuffix = useMemo(() => {
        return component.suffix ? component.suffix : "";
    }, [component.suffix]);

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        };
    }, []);

    useEffect(() => {
        return () => {
            const recordChanges: Record<string, any> = {};
            const suffix = component.suffix ? component.suffix : "";
            onValidationRegister(() => {
                return Promise.resolve(true);
            }, `counterparty_${suffix}`);

            Object.keys(VendorFields).forEach((key) => {
                recordChanges[`${key}${suffix}`] = undefined;
            });
            onChange(recordChanges);
        };
    }, []);

    useEffect(() => {
        const suffix = component.suffix ? component.suffix : "";
        const externalInn = externalUpdateRecord[`${VendorFields.vendorInn}${suffix}`];
        const externalVendorName = externalUpdateRecord[`${VendorFields.vendor}${suffix}`];
        if (externalInn) {
            setInn(externalInn);
            setUseExternalValues(true);
        } else {
            if (externalVendorName) {
                loadVendorOptions(externalVendorName, (options: OptionType[]) => {
                    const option = options.find(o => o.label === externalVendorName);
                    setVendorName(option);
                    setUseExternalValues(true);
                });
            } else {
                setInn("");
                setVendorName(undefined);
            }
        }
    }, [externalUpdateRecord]);

    useEffect(() => {
        const recordChanges: Record<string, any> = {};
        const suffix = component.suffix ? component.suffix : "";
        Object.keys(VendorFields).forEach((key) => {
            recordChanges[`${key}${suffix}`] = record[key] ? record[key] : undefined;
        });
        onChange(recordChanges);
    }, [record, component.suffix]);

    useEffect(() => {
        if (vendorName) {
            const updateObj: Record<string, any> = {
                vendor: vendorName.label
            };
            const fieldsToUpdate = [VendorFields.vendor];
            if (vendorName.value) {
                updateObj[VendorFields.vendorInn] = vendorName.value;
                fieldsToUpdate.push(VendorFields.vendorInn);
                if (inn !== vendorName.value) {
                    setInn(vendorName.value);
                }
            }
            updateRecordWithObject(updateObj, fieldsToUpdate);
        } else {
            setRecord({ [VendorFields.vendorInn]: inn });
            setVendorSite(undefined);
            setVendorSiteOptions([]);
            clearBankDetails();
        }
    }, [vendorName]);

    useEffect(() => {
        setShowInnMessage(false);
        if (inn && inn.length > 7) {
            setRecord({ vendorInn: inn });
            clearBankDetails();
            setVendorSiteOptions([]);
            setVendorSite(undefined);
            setAddress(undefined);
            setAddressOptions([]);
            setLoading(true);
            documentService.getVendor(inn, extUserId, globalCache).then((res) => {
                if (res.vendor) {
                    setVendor(res);
                    setVendorName({ value: res.vendorInn, label: res.vendor });
                    updateRecordWithObject({ vendorEngName: res.vendorEngName }, [VendorFields.vendorEngName]);
                    const addressSet = new Set<string>();
                    res.vendorAddress.forEach((a) => {
                        addressSet.add(a.address);
                    });
                    const addressList = Array.from(addressSet);
                    if (addressList && addressList.length > 0) {
                        const addressOptions = addressList.map((address) => {
                            return {
                                value: address,
                                label: address,
                            };
                        });
                        setAddressOptions(addressOptions);
                        if (addressOptions.length > 0) {
                            updateRecordWithObject({ vendorAddress: addressOptions[0].label }, [VendorFields.vendorAddress]);
                        }
                    }
                    if (res.vendorSites && res.vendorSites.length > 0) {
                        const vendorSitesSet = new Set<string>();
                        res.vendorSites.forEach((site) => {
                            vendorSitesSet.add(site.vendorSiteCode);
                        });
                        const vendorSiteList = Array.from(vendorSitesSet);
                        const siteOptions = vendorSiteList.map((s) => {
                            return {
                                value: s,
                                label: s,
                            };
                        });
                        setVendorSiteOptions(siteOptions);
                        if (siteOptions.length > 0) {
                            setVendorSite(siteOptions[0]);
                        }
                    }
                    if (res.vendorBanks && res.vendorBanks.length > 0) {
                        updateBankDetails(res.vendorBanks);
                    } else {
                        clearBankDetails();
                    }
                } else {
                    if (useExternalValues) {
                        const suffix = component.suffix ? component.suffix : "";
                        const externalVendorName = externalUpdateRecord[`${VendorFields.vendor}${suffix}`];
                        loadVendorOptions(externalVendorName, (options: OptionType[]) => {
                            const option = options.find(o => o.label === externalVendorName);
                            setVendorName(option);
                        });
                    } else {
                        setVendorName(undefined);
                        setShowInnMessage(true);
                    }
                }
                if (useExternalValues) {
                    setUseExternalValues(false);
                    setTimeout(() => {
                        const updateChanges: Record<string, any> = {};
                        const suffix = component.suffix ? component.suffix : "";
                        component.fieldsList?.forEach((key) => {
                            if ([CounterpartyDocumentSelectFields.vendorAddress,
                                CounterpartyDocumentSelectFields.vendorSite,
                                CounterpartyDocumentSelectFields.vendorBankName,
                                CounterpartyDocumentSelectFields.vendorBic,
                                CounterpartyDocumentSelectFields.vendorCorrAccount,
                                CounterpartyDocumentSelectFields.vendorPaymentAccount
                            ].includes(key as CounterpartyDocumentSelectFields)) {
                                const option = externalUpdateRecord[`${key}${suffix}`] ? {
                                    value: externalUpdateRecord[`${key}${suffix}`],
                                    label: externalUpdateRecord[`${key}${suffix}`]
                                } : undefined;

                                const [value, setValue, options] = selectVariablesMapping[key as CounterpartyDocumentSelectFields];
                                setValue(option);
                            }
                            updateChanges[key] = externalUpdateRecord[`${key}${suffix}`] ? externalUpdateRecord[`${key}${suffix}`] : undefined;
                        });
                        setRecord(updateChanges);
                        Object.keys(VendorFields).forEach((key) => {
                            const val = externalUpdateRecord[`${key}${suffix}`] ? externalUpdateRecord[`${key}${suffix}`] : undefined;
                            updateChanges[key] = val;
                            setTimeout(() => {
                                updateRecordWithObject({ [key]: val }, [key]);
                            }, 100);
                        });
                    }, 200);
                }
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [inn]);

    useEffect(() => {
        if (bankName && bankDetails.length > 0) {
            const bankBic = bankDetails.find(detail => detail.bankName === bankName.value)?.bic;
            if (bankBic && bic?.value !== bankBic) {
                setBic(constructOptions([bankBic])[0]);
            }
        }
        updateRecordWithObject({ vendorBankName: bankName?.value }, [VendorFields.vendorBankName]);
    }, [bankName, bankDetails]);

    useEffect(() => {
        if (bic && bankDetails.length > 0) {
            const paymentAccountOpts = new Set<string>();
            const corrAccountOpts = new Set<string>();
            const filteredBankDetails = bankDetails.filter(detail => detail.bic === bic.value);
            filteredBankDetails.forEach((detail) => {
                paymentAccountOpts.add(detail.paymentAccount);
                corrAccountOpts.add(detail.correspondentAccount);
            });
            const corrAccountOptions = constructOptions(Array.from(corrAccountOpts));
            const payAccountOptions = constructOptions(Array.from(paymentAccountOpts));
            setCorrAccountOptions(corrAccountOptions);
            setPaymentAccountOptions(payAccountOptions);
            if (!paymentAccount?.value || !payAccountOptions.map(o => o.value).includes(paymentAccount.value)) {
                setPaymentAccount(payAccountOptions[0]);
            }
            if (!corrAccount?.value || !corrAccountOptions.map(o => o.value).includes(corrAccount.value)) {
                setCorrAccount(corrAccountOptions[0]);
            }
            const bName = bankDetails.find(detail => detail.bic === bic.value)?.bankName;
            if (bName && bankName?.value !== bName) {
                setBankName(constructOptions([bName])[0]);
            }
        }
        updateRecordWithObject({ vendorBic: bic?.value }, [VendorFields.vendorBic]);
    }, [bic, bankDetails]);

    useEffect(() => {
        updateRecordWithObject({ vendorPaymentAccount: paymentAccount?.value }, [VendorFields.vendorPaymentAccount]);
    }, [paymentAccount]);

    useEffect(() => {
        updateRecordWithObject({ vendorCorrAccount: corrAccount?.value }, [VendorFields.vendorCorrAccount]);
    }, [corrAccount]);

    useEffect(() => {
        updateRecordWithObject({ vendorAddress: address?.value }, [VendorFields.vendorAddress]);
    }, [address]);

    useEffect(() => {
        const vendorSiteRecord = vendor?.vendorSites.find(v => v.vendorSiteId.toString() === vendorSite?.value || v.vendorSiteCode === vendorSite?.value);
        const updateObj: Record<string, any> = {
            vendorSite: vendorSiteRecord ? vendorSiteRecord.vendorSiteCode : undefined
        };
        const fieldsToUpdate = [VendorFields.vendorSite];
        if (vendorSiteRecord && vendorSiteRecord.kpp) {
            updateObj[VendorFields.vendorKpp] = vendorSiteRecord.kpp;
            fieldsToUpdate.push(VendorFields.vendorKpp);
        } else {
            if (!readonly) {
                fieldsToUpdate.push(VendorFields.vendorKpp);
            }
        }

        updateRecordWithObject(updateObj, fieldsToUpdate);
    }, [vendorSite, vendor]);

    const handleClearEvent = () => {
        if (!isExternalProtected) {
            setInn("");
            setVendorName(undefined);
            setRecord({});
        }
    };

    const clearBankDetails = () => {
        setBankDetails([]);
        setBankName(undefined);
        setBic(undefined);
        setPaymentAccount(undefined);
        setCorrAccount(undefined);
        setBankNameOptions([]);
        setBicOptions([]);
        setPaymentAccountOptions([]);
        setCorrAccountOptions([]);
    };

    const updateBankDetails = (vendorBankDetails: IVendorBank[]) => {
        setBankDetails(vendorBankDetails);
        const bankNameOpts = new Set<string>();
        const bicOpts = new Set<string>();
        vendorBankDetails.forEach((detail) => {
            bankNameOpts.add(detail.bankName);
            bicOpts.add(detail.bic);
        });
        const uniqueBankOptions = constructOptions(Array.from(bankNameOpts));
        const uniqueBicOptions = constructOptions(Array.from(bicOpts));
        setBankNameOptions(uniqueBankOptions);
        setBicOptions(uniqueBicOptions);
        if (uniqueBankOptions.length > 0) {
            setBankName(uniqueBankOptions[0]);
        }
    };

    const updateRecordWithObject = (updateObj: Record<string, any> | undefined, fieldsToUpdate: string[]) => {
        const update: Record<string, string | undefined> = {};
        fieldsToUpdate.forEach((f) => {
            update[f] = updateObj ? updateObj[f] : undefined;
        });
        setRecord((prevState) => {
            return { ...prevState, ...update };
        });
    };

    const loadVendorOptions = debounce((inputValue: string, callback: (options: any) => void) => {
        if (!!inputValue) {
            documentService.getVendorNames(inputValue, extUserId)
                .then(response => {
                    if (response.length > 0) {
                        const options: OptionType[] = [];
                        response.forEach((vendor) => {
                            options.push({
                                value: vendor.id,
                                label: vendor.name,
                            });
                        });
                        callback(options);
                    } else {
                        callback((isExternal || isCreatable) ? [{
                            value: undefined,
                            label: inputValue
                        }] : []);
                    }
                })
                .catch(() => callback([]));
        } else {
            callback([]);
        }
    }, 200);

    const required = useMemo(() => {
        return component.required !== false;
    }, [component]);

    const validateFields = (): boolean => {
        const vendorFields = Object.keys(VendorFields);
        return !!component.fieldsList?.every(field => {
            return !!record[field] || !vendorFields.includes(field);
        });
    };

    useEffect(() => {
        onValidationRegister(validateForm, `counterparty_${component.suffix ? component.suffix : ""}`);
    }, [component.suffix, record, required]);


    const validateForm = (): Promise<boolean> => {
        let valid = true;
        if (required) {
            valid = validateFields();
        }
        return Promise.resolve(valid);
    };
    const onFieldChange = (field: string, value: string | undefined) => {
        setRecord((prevState) => {
            return { ...prevState, [field]: value };
        });
    };

    const isShowSelect = (field: string) => {
        const isSelectField = selectFields.includes(field);
        if (isSelectField) {
            if (field === CounterpartyDocumentSelectFields.vendor) {
                return true;
            }
            const [value, setValue, options] = selectVariablesMapping[field as CounterpartyDocumentSelectFields];
            return options.length > 1;
        } else {
            return false;
        }
    };

    const validateFunction = useCallback((value: string): IValidateError => {
        if (required && !value) {
            return {
                valid: false,
                message: t("requiredFieldError")
            };
        }

        return {
            valid: true
        };
    }, [component, i18n.language]);

    const innValidateFunction = useCallback((value: string): IValidateError => {
        if (required && !value) {
            return {
                valid: false,
                message: t("requiredFieldError")
            };
        }
        if (value.length < 8) {
            return {
                valid: false,
                message: t("minStringLength", { minStringLength: 8 })
            };
        }
        return {
            valid: true
        };
    }, [component, i18n.language]);

    const sortFieldList = (fieldList?: string[]): string[] => {
        const updatedFieldList: string[] = [];
        if (!fieldList) {
            return updatedFieldList;
        } else {
            Object.values(VendorFields).forEach((field) => {
                if (fieldList.includes(field)) {
                    updatedFieldList.push(field);
                }
            });

            return updatedFieldList;
        }
    };

    const renderSelect = (field: CounterpartyDocumentSelectFields) => {
        const [value, setValue, options] = selectVariablesMapping[field];
        return field === CounterpartyDocumentSelectFields.vendor ?
            <>
                <AsyncSearchableSelect
                    value={value}
                    loadOptions={loadVendorOptions}
                    isMulti={false}
                    disabled={isExternalProtected}
                    hideClearIcon={false}
                    onChange={(o) => {
                        setValue(o ? o as OptionType : undefined);
                    }}/>
                {showInnMessage && !isExternal && <div className={styles.innMessageContainer}>
                    <div className={styles.innMessageTitle}>{t("createCounterpartyTitle")}</div>
                    <div>{t("counterpartyNonResident")}<a target="_blank" rel="noreferrer" className={styles.vendorLink}
                                                          href={getProperty("createNoneRezVendor")}>{t("here")}</a>
                    </div>
                    <div>{t("counterpartyIndividual")}<a target="_blank" rel="noreferrer" className={styles.vendorLink}
                                                         href={getProperty("createFlVendor")}>{t("here")}</a>
                    </div>
                    <div>{t("counterpartyResident")}<a target="_blank" rel="noreferrer" className={styles.vendorLink}
                                                       href={getProperty("createVendor")}>{t("here")}</a>
                    </div>
                </div>}
            </> : <SearchableSelect
                optionValue={value}
                validate={required}
                triggerValidate={triggerValidate}
                options={options}
                isMulti={false}
                disabled={isExternalProtected}
                isCreatable={isCreatable}
                isClearable={true}
                onOptionChange={(o) => setValue(o ? o as OptionType : undefined)}
            />;
    };

    return <div className={styles.componentContainer}>
        <div className={styles.titleContainer}>
            <div className={styles.title}>{label}</div>
            <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltipContainer}/>
        </div>
        <div className={styles.container}>
            {sortFieldList(component.fieldsList).map((field: string) =>
                <FormLabel
                    title={t(`vendorFields.${field as VendorFields}`)}
                    key={field}
                    disabled={isExternalProtected || ((readonly || (selectFields.includes(field) && component.selectonly)) && field !== VendorFields.vendorInn && !isShowSelect(field))}
                    required={required}>
                    {isShowSelect(field) ?
                        renderSelect(field as CounterpartyDocumentSelectFields) :
                        <DynamicInput size="m"
                                      textareaEdge={70}
                                      name={field + componentSuffix}
                                      containerClassName={styles.input}
                                      validate={required || (field === VendorFields.vendorInn && !!record[field])}
                                      validateFunc={field === VendorFields.vendorInn ? innValidateFunction : validateFunction}
                                      triggerValidate={triggerValidate}
                                      disabled={((readonly || (selectFields.includes(field) && component.selectonly)) && field !== VendorFields.vendorInn) || isExternalProtected}
                                      value={record[field] ? record[field] as string : ""}
                                      onChange={(e: any) => {
                                          if (field === VendorFields.vendorInn) {
                                              setInn(e.target.value ? e.target.value : "");
                                          }
                                          onFieldChange(field, e.target.value ? e.target.value : undefined);
                                      }
                                      }

                                      view="outline"/>
                    }
                    {loading && field === "vendorInn" && <LinearProgress className={styles.loading}/>}
                </FormLabel>)}
        </div>
    </div>;
});
